/**
 * IndexedDB-Entwürfe und Autosave-Helfer (Paket 14).
 *
 * Kein Netzaufruf in diesem Modul — nur lokaler Speicher und Zustandsentscheidungen.
 */

const DB_NAME = "cubic-seiten";
const DB_VERSION = 1;
const STORE_ENTWURF = "seitenEntwuerfe";
const STORE_META = "metadaten";

export type ServerBasis = {
  version: number;
  titel: string;
  inhalt: string;
  fingerabdruck: string;
};

export type Ausstehend = {
  aenderungsId: string;
  lokaleRevision: number;
  erwarteteVersion: number;
  titel: string;
  inhalt: string;
  fingerabdruck: string;
};

export type SeitenEntwurf = {
  schemaVersion: 1;
  personId: string;
  projektId: string;
  seiteId: string;
  geraetId: string;
  titel: string;
  inhalt: string;
  lokaleRevision: number;
  lokalGesichertAm: number;
  basis: ServerBasis;
  ausstehend?: Ausstehend;
  letzteServerBestaetigung?: {
    lokaleRevision: number;
    version: number;
    fingerabdruck: string;
    bestaetigtAm: number;
  };
};

export type SpeicherStatus =
  | "gespeichert"
  | "lokal_wird_gesichert"
  | "lokal_gesichert"
  | "wird_gespeichert"
  | "offline"
  | "konflikt"
  | "nicht_sicher";

export type RecoveryEntscheidung =
  | { art: "server" }
  | { art: "entwurf_loeschen" }
  | { art: "lokal_wiederherstellen"; banner: true }
  | { art: "ausstehend_wiederholen" }
  | { art: "offline"; entwurf: SeitenEntwurf }
  | { art: "konflikt"; entwurf: SeitenEntwurf; serverVersion: number }
  | { art: "zugriff_entzogen"; entwurf: SeitenEntwurf };

function oeffneDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const anfrage = indexedDB.open(DB_NAME, DB_VERSION);
    anfrage.onupgradeneeded = () => {
      const db = anfrage.result;
      if (!db.objectStoreNames.contains(STORE_ENTWURF)) {
        db.createObjectStore(STORE_ENTWURF, {
          keyPath: ["personId", "seiteId"],
        });
      }
      if (!db.objectStoreNames.contains(STORE_META)) {
        db.createObjectStore(STORE_META, { keyPath: "schluessel" });
      }
    };
    anfrage.onsuccess = () => resolve(anfrage.result);
    anfrage.onerror = () =>
      reject(anfrage.error ?? new Error("IndexedDB nicht verfügbar"));
  });
}

function txDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error("IndexedDB-Transaktion"));
    tx.onabort = () => reject(tx.error ?? new Error("IndexedDB abgebrochen"));
  });
}

export async function liesOderErzeugeGeraetId(): Promise<string> {
  const db = await oeffneDb();
  try {
    const lesen = db.transaction(STORE_META, "readonly");
    const store = lesen.objectStore(STORE_META);
    const vorhanden = await new Promise<{ wert: string } | undefined>(
      (resolve, reject) => {
        const r = store.get("geraetId");
        r.onsuccess = () =>
          resolve(r.result as { wert: string } | undefined);
        r.onerror = () => reject(r.error);
      },
    );
    await txDone(lesen);
    if (vorhanden?.wert) return vorhanden.wert;

    const id = crypto.randomUUID();
    const schreiben = db.transaction(STORE_META, "readwrite");
    schreiben.objectStore(STORE_META).put({ schluessel: "geraetId", wert: id });
    await txDone(schreiben);
    return id;
  } finally {
    db.close();
  }
}

export async function liesEntwurf(
  personId: string,
  seiteId: string,
): Promise<SeitenEntwurf | null> {
  const db = await oeffneDb();
  try {
    const tx = db.transaction(STORE_ENTWURF, "readonly");
    const r = tx.objectStore(STORE_ENTWURF).get([personId, seiteId]);
    const wert = await new Promise<SeitenEntwurf | undefined>(
      (resolve, reject) => {
        r.onsuccess = () => resolve(r.result as SeitenEntwurf | undefined);
        r.onerror = () => reject(r.error);
      },
    );
    await txDone(tx);
    return wert ?? null;
  } finally {
    db.close();
  }
}

export async function schreibeEntwurf(entwurf: SeitenEntwurf): Promise<void> {
  const db = await oeffneDb();
  try {
    const tx = db.transaction(STORE_ENTWURF, "readwrite");
    tx.objectStore(STORE_ENTWURF).put(entwurf);
    await txDone(tx);
  } finally {
    db.close();
  }
}

export async function loescheEntwurf(
  personId: string,
  seiteId: string,
): Promise<void> {
  const db = await oeffneDb();
  try {
    const tx = db.transaction(STORE_ENTWURF, "readwrite");
    tx.objectStore(STORE_ENTWURF).delete([personId, seiteId]);
    await txDone(tx);
  } finally {
    db.close();
  }
}

/** Recovery beim Öffnen einer Seite. */
export function entscheideRecovery(optionen: {
  entwurf: SeitenEntwurf | null;
  server: ServerBasis | null;
  serverErreichbar: boolean;
  zugriff: boolean;
}): RecoveryEntscheidung {
  const { entwurf, server, serverErreichbar, zugriff } = optionen;

  if (!zugriff && entwurf) {
    return { art: "zugriff_entzogen", entwurf };
  }
  if (!entwurf) return { art: "server" };
  if (!serverErreichbar) return { art: "offline", entwurf };
  if (!server) return { art: "offline", entwurf };

  if (entwurf.ausstehend) {
    return { art: "ausstehend_wiederholen" };
  }

  if (
    entwurf.basis.version === server.version &&
    entwurf.basis.fingerabdruck === server.fingerabdruck &&
    entwurf.titel === server.titel &&
    entwurf.inhalt === server.inhalt
  ) {
    return { art: "entwurf_loeschen" };
  }

  if (server.version > entwurf.basis.version) {
    return {
      art: "konflikt",
      entwurf,
      serverVersion: server.version,
    };
  }

  if (
    entwurf.basis.version === server.version &&
    (entwurf.titel !== server.titel || entwurf.inhalt !== server.inhalt)
  ) {
    return { art: "lokal_wiederherstellen", banner: true };
  }

  return { art: "server" };
}

/** Trailing-Timer-Hilfen: lokal 250/1000 ms, Server 1200/5000 ms. */
export const AUTOSAVE = {
  lokalTrailingMs: 250,
  lokalMaxMs: 1_000,
  serverTrailingMs: 1_350,
  serverMaxMs: 5_000,
} as const;
